import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { OrnamentalFrame } from "@/components/Mughal";
import MotionBackdrop from "@/components/MotionBackdrop";
import {
  MusicBand, FilmBand, DesignsBand, BollydollBand,
  MystBand, BooksBand, PressBand, ContactBand,
} from "@/components/VentureBands";

const VENTURES = [
  { n: "01", label: "Music", href: "/music", note: "Indian Music Catalogue", internal: true },
  { n: "02", label: "Film & TV", href: "http://highgateent.com/", note: "Highgate Entertainment" },
  { n: "03", label: "Designs", href: "https://www.amritasendesigns.com/", note: "Amrita Sen Designs" },
  { n: "04", label: "BollyDoll", href: "https://bollydoll.com/", note: "the world of Rani & Sheena" },
  { n: "05", label: "MystMusic", href: "https://mystmusic.io/", note: "see the studios" },
  { n: "06", label: "Books", href: "/books", note: "stories in print", internal: true },
  { n: "07", label: "Press", href: "https://www.amritasen.com/press.html", note: "features & interviews" },
  { n: "08", label: "Contact", href: "https://www.amritasen.com/amrita-sen-contact.html", note: "write to the studio" },
];

const VentureIndex = () => {
  const [open, setOpen] = useState(null);

  const bandFor = (label) => {
    switch (label) {
      case "Music": return <MusicBand />;
      case "Film & TV": return <FilmBand />;
      case "Designs": return <DesignsBand />;
      case "BollyDoll": return <BollydollBand />;
      case "MystMusic": return <MystBand />;
      case "Books": return <BooksBand />;
      case "Press": return <PressBand />;
      case "Contact": return <ContactBand />;
      default: return null;
    }
  };

  return (
    <section id="elsewhere" data-testid="venture-index" className="relative px-6 pb-0 pt-8 md:pt-12">
      <div className="relative mx-auto w-full max-w-4xl">
        <OrnamentalFrame>
          <div data-testid="motion-art-backdrop" className="relative overflow-hidden">
            <MotionBackdrop />
            <ul className="relative flex flex-col px-6 py-6 md:px-12 md:py-10">
              {VENTURES.map((v, i) => (
                <motion.li
                  key={v.label}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.7, delay: i * 0.05, ease: "easeOut" }}
                  className="border-b border-[#d4af37]/15 last:border-b-0"
                  onMouseEnter={() => setOpen(i)}
                  onMouseLeave={() => setOpen(null)}
                >
                  <a
                    data-testid={`venture-link-${v.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    href={v.href}
                    {...(v.internal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
                    onClick={(e) => {
                      // first tap opens the signature motion; second tap follows the link
                      if (open !== i) {
                        e.preventDefault();
                        setOpen(i);
                      }
                    }}
                    className="group flex items-baseline gap-5 py-4 md:gap-8 md:py-5"
                  >
                    <span className="font-serif-display text-xs italic text-[#a89a80]/70 transition-colors duration-500 group-hover:text-[#d4af37]/80">
                      {v.n}
                    </span>
                    <span
                      className="font-serif-display text-2xl font-light uppercase text-[#f3ead6]/90 transition-all duration-500 group-hover:translate-x-2 group-hover:text-[#d4af37] md:text-3xl"
                      style={{ letterSpacing: "0.1em" }}
                    >
                      {v.label}
                    </span>
                    <span
                      className="ml-auto hidden text-right text-[11px] text-[#a89a80]/80 transition-colors duration-500 group-hover:text-[#a89a80] sm:block"
                      style={{ letterSpacing: "0.18em" }}
                    >
                      {v.note}
                    </span>
                  </a>
                  <AnimatePresence initial={false}>
                    {open === i && (
                      <motion.div
                        data-testid={`venture-band-${v.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="relative mb-4 h-28 overflow-hidden border border-[#d4af37]/20 bg-[#170e05] md:h-36">
                          {bandFor(v.label)}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.li>
              ))}
            </ul>
          </div>
        </OrnamentalFrame>
      </div>
    </section>
  );
};

export default VentureIndex;
